import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "../lib/site";

const LogoMark = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
    <defs>
      <linearGradient id="navbg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#232F52" />
        <stop offset="1" stopColor="#0B132B" />
      </linearGradient>
      <path id="navr" d="M16 46V18h10.5c4.6 0 7.7 2.6 7.7 6.7 0 3-1.6 5.2-4.3 6.2L35 46h-6l-4.4-13.4H21.6V46H16zm5.6-17.8h4.5c2 0 3.3-1.1 3.3-3s-1.3-2.9-3.3-2.9h-4.5v5.9z" />
    </defs>
    <rect width="64" height="64" rx="14" fill="url(#navbg)" stroke="rgba(245,158,11,0.4)" strokeWidth="1.5" />
    <use href="#navr" transform="translate(5.3 9) scale(0.72)" fill="#F7F5F0" />
    <use href="#navr" transform="translate(22 9) scale(0.72)" fill="#F59E0B" />
  </svg>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-navy/95 backdrop-blur-md border-b border-white/10" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo-link">
            <LogoMark />
            <span className="leading-none">
              <span className="block font-display text-2xl sm:text-3xl font-black tracking-tight text-white group-hover:text-gold-light transition-colors duration-300">
                RICHIE <span className="text-gold">RICH</span>
              </span>
              <span className="block text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-slate-400 mt-1">
                Enterprise IT Solutions
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-quote-cta"
              className="inline-flex items-center gap-2 bg-gold text-navy font-bold text-sm px-5 py-2.5 rounded-full hover:bg-gold-light hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-gold/20"
            >
              Get a Quote
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </nav>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-white/10 bg-navy px-5 py-4 space-y-1" data-testid="mobile-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `block py-3 px-3 rounded-lg font-semibold ${
                  isActive ? "text-gold bg-white/5" : "text-slate-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-quote-cta"
            className="mt-2 flex items-center justify-center gap-2 bg-gold text-navy font-bold px-5 py-3 rounded-full"
          >
            Get a Quote
            <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
